import { BridgeComponent } from '@hotwired/hotwire-native-bridge';

type NavigationBarMode = 'visible' | 'hidden' | 'immersive';

/**
 * Chooses how the native navigation bar and the area under the status bar are used while this element is on the
 * page. Does nothing in a browser. Attach it with data-controller="bridge--navigation-bar" and pick a mode with
 * data-bridge--navigation-bar-mode-value (default "hidden"):
 *
 * - visible:   the usual navigation bar. Sent when the element goes away.
 * - hidden:    no navigation bar; the page stays inside the safe area.
 * - immersive: no navigation bar, and the page extends under the status bar. The page then has to keep its content
 *              clear of it with env(safe-area-inset-top), and needs viewport-fit=cover in its viewport meta tag for
 *              that value to be reported.
 *
 * A visible bar can match the page with data-bridge--navigation-bar-background-value and -tint-value (the colour of
 * the title, back button and other items). Each is a colour, or a CSS custom property such as "--bs-primary" so the
 * colours stay in the stylesheets.
 */
export default class NavigationBarController extends BridgeComponent {
  public static component = 'navigation-bar';
  public static values = {
    mode: { type: String, default: 'hidden' },
    background: { type: String, default: '' },
    tint: { type: String, default: '' }
  };

  declare private readonly modeValue: NavigationBarMode;
  declare private readonly backgroundValue: string;
  declare private readonly tintValue: string;

  public connect(): void {
    this.send('mode', {
      mode: this.modeValue,
      background: this._resolveColor(this.backgroundValue),
      tint: this._resolveColor(this.tintValue)
    });
  }

  public disconnect(): void {
    this.send('mode', { mode: 'visible' });
  }

  private _resolveColor(value: string): string | undefined {
    if (!value.startsWith('--')) {
      return value || undefined;
    }

    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue(value)
        .trim() || undefined
    );
  }
}
